/**
 * As regras do editor de ligações que não dependem de tela nem de rede.
 *
 * **Este arquivo não importa nada em tempo de execução** — só tipos. É de
 * propósito: assim ele roda direto no Node (`testes/editor.test.ts`) e as
 * regras são provadas sem navegador e sem API.
 *
 * O servidor refaz as mesmas conferências antes de gravar — e é ele quem manda.
 * As daqui existem para o operador saber NA HORA, ao clicar, que aquilo vai ser
 * recusado, em vez de descobrir depois de montar um lote inteiro.
 */

import type {
  ArestaDoGrafo, ConferenciaDoNome, GrafoDoPinout, NoDoGrafo, NovoPino, OperacaoDoEditor, RamoDaJuncao,
} from "../../../../api/editorDeLigacoes"

// ------------------------------------------------------------------ a regra do nome

/** O nome sem a etiqueta: tudo antes do primeiro `#`. */
export function base(circuito: string): string {
  const i = circuito.indexOf("#")
  return (i < 0 ? circuito : circuito.slice(0, i)).trimEnd()
}

/** Até o primeiro ponto, inclusive — metade da regra da junção no motor. */
export function prefixo(circuito: string): string {
  const i = circuito.indexOf(".")
  return i < 0 ? "" : circuito.slice(0, i + 1)
}

/** Do `#` em diante — a etiqueta. */
export function etiqueta(circuito: string): string {
  const i = circuito.indexOf("#")
  return i < 0 ? "" : circuito.slice(i).trim()
}

export const temEtiqueta = (circuito: string) => circuito.includes("#")

/**
 * Os ramos de uma junção precisam começar igual antes do ponto. Devolve o
 * prefixo comum ou, quando não há, o motivo — escrito para o operador.
 */
export function prefixoComum(circuitos: string[]): { prefixo: string } | { motivo: string } {
  const bases = circuitos.map(base)
  if (bases.some((b) => b.length === 0)) return { motivo: "há pino sem nome de circuito antes do #" }
  const semPonto = bases.filter((b) => prefixo(b).length === 0)
  if (semPonto.length > 0) {
    return { motivo: `há circuito sem ponto no nome (${[...new Set(semPonto)].join(", ")}) — sem o ponto não existe a família que junta os ramos` }
  }
  const distintos = [...new Set(bases.map((b) => prefixo(b).toUpperCase()))]
  if (distintos.length > 1) {
    return { motivo: `os ramos precisam começar igual antes do ponto; aqui há ${[...new Set(bases)].sort().join(", ")}` }
  }
  return { prefixo: prefixo(bases[0]!) }
}

// ------------------------------------------------------------------ o laço

/**
 * O caminho entre dois nós, sem olhar a direção — fio não tem sentido. `null`
 * quando não se ligam. `ignorar` tira arestas da conta.
 */
export function caminho(
  arestas: ArestaDoGrafo[], de: string, para: string, ignorar?: (a: ArestaDoGrafo) => boolean,
): string[] | null {
  const vizinhos = new Map<string, string[]>()
  const ligar = (a: string, b: string) => {
    const l = vizinhos.get(a)
    if (l) l.push(b)
    else vizinhos.set(a, [b])
  }
  for (const a of arestas) {
    if (ignorar?.(a)) continue
    ligar(a.de, a.para)
    ligar(a.para, a.de)
  }
  const veioDe = new Map<string, string | null>([[de, null]])
  const fila = [de]
  while (fila.length > 0) {
    const atual = fila.shift()!
    if (atual === para) {
      const rota: string[] = []
      for (let n: string | null | undefined = atual; n != null; n = veioDe.get(n)) rota.push(n)
      return rota.reverse()
    }
    for (const v of vizinhos.get(atual) ?? []) {
      if (!veioDe.has(v)) { veioDe.set(v, atual); fila.push(v) }
    }
  }
  return null
}

/** `ECM.A1 → J1.2 → Injetor.1`, para a mensagem dizer por onde o laço fecha. */
export function descreverCaminho(g: GrafoDoPinout, ids: string[]): string {
  const porId = new Map(g.nos.map((n) => [n.id, n]))
  return ids.map((id) => {
    const n = porId.get(id)
    return n ? `${n.componenteCodigo || n.componente} ${n.conector}·${n.pino}` : id
  }).join(" → ")
}

/**
 * A conferência de antes de enfileirar. Devolve o motivo da recusa, ou `null`.
 * O servidor confere de novo — inclusive o que só ele sabe, como o achado novo
 * que a alteração criaria.
 */
export function verificar(g: GrafoDoPinout, op: OperacaoDoEditor): string | null {
  const porId = new Map(g.nos.map((n) => [n.id, n]))
  switch (op.tipo) {
    case "ligarExistente": {
      if (op.deId === op.paraId) return "Um pino não se liga nele mesmo."
      const de = porId.get(`p${op.deId}`)
      const para = porId.get(`p${op.paraId}`)
      if (!de || !para) return "Os dois pinos precisam estar no desenho."
      if (de.circuitoId === para.circuitoId) return "Os dois pinos já estão no mesmo circuito."
      const rota = caminho(g.arestas, de.id, para.id)
      if (rota) {
        return `${para.componente} ${para.conector}·${para.pino} já chega em ${de.componente} ${de.conector}·${de.pino} ` +
          `(${descreverCaminho(g, rota)}). Ligar os dois fecharia um laço — o circuito voltaria nele mesmo.`
      }
      return null
    }
    case "marcarJuncao": {
      const ids = [...new Set(op.pinoutIds)]
      if (ids.length < 2) return "Uma junção precisa de pelo menos dois pinos."
      const nos = ids.map((id) => porId.get(`p${id}`))
      if (nos.some((n) => !n || n.componenteId !== op.componenteId)) return "Junção é sempre dentro de um componente só."
      const p = prefixoComum(nos.map((n) => n!.circuito))
      if ("motivo" in p) return `Não dá para marcar a junção: ${p.motivo}.`
      const daJuncao = new Set(ids.map((id) => `p${id}`))
      const ignorar = (a: ArestaDoGrafo) => a.tipo === "juncao" && daJuncao.has(a.de) && daJuncao.has(a.para)
      for (let i = 0; i < ids.length; i++) {
        for (let j = i + 1; j < ids.length; j++) {
          const rota = caminho(g.arestas, `p${ids[i]}`, `p${ids[j]}`, ignorar)
          if (rota) {
            return `Os pinos ${nos[i]!.conector}·${nos[i]!.pino} e ${nos[j]!.conector}·${nos[j]!.pino} já se ligam por fora da junção ` +
              `(${descreverCaminho(g, rota)}). Marcar a junção fecharia um laço.`
          }
        }
      }
      return null
    }
    case "soltarEtiqueta": {
      const n = porId.get(`p${op.pinoutId}`)
      if (!n) return "O pino precisa estar no desenho."
      if (!temEtiqueta(n.circuito)) return `O circuito '${n.circuito}' não tem etiqueta para soltar.`
      if (base(n.circuito).length === 0) {
        return `O circuito '${n.circuito}' não tem nome antes do # — soltar deixaria o circuito vazio, que puxa o projeto inteiro.`
      }
      return null
    }
    case "apagarPino":
      return porId.has(`p${op.pinoutId}`) ? null : "O pino precisa estar no desenho."
    // O formulário já conferiu o que dá para conferir sem o banco; o resto é do servidor.
    case "criarPino":
      return null
    case "criarJuncao": {
      // Duas pontas que já existem e já se alcançam, juntas pela junção, fechariam um laço.
      const pontas = op.ramos.filter((r) => r.pontaPinoutId > 0).map((r) => porId.get(`p${r.pontaPinoutId}`))
      for (let i = 0; i < pontas.length; i++) {
        for (let j = i + 1; j < pontas.length; j++) {
          const a = pontas[i]
          const b = pontas[j]
          if (!a || !b) continue
          if (a.circuitoId === b.circuitoId) {
            return `${a.componenteCodigo || a.componente} ${a.conector}·${a.pino} e ${b.componenteCodigo || b.componente} ${b.conector}·${b.pino} ` +
              `já estão no mesmo fio ('${a.circuito}'): pela junção fechariam um laço.`
          }
          const rota = caminho(g.arestas, a.id, b.id)
          if (rota) {
            return `${b.componenteCodigo || b.componente} ${b.conector}·${b.pino} já chega em ${a.componenteCodigo || a.componente} ${a.conector}·${a.pino} ` +
              `(${descreverCaminho(g, rota)}). Juntá-los pela junção fecharia um laço.`
          }
        }
      }
      return null
    }
  }
}

// ------------------------------------------------------------------ o fio novo

/** Um catálogo como o campo de busca entrega: `id` quando veio da lista, só `texto` quando é novo. */
export type Escolha = { id: number; texto: string }
export const NADA: Escolha = { id: 0, texto: "" }

/** Uma ponta do fio: onde ele encosta. */
export type PontaDoFio = {
  componente: Escolha
  /** Só vale para componente novo (Q8). */
  tipoComponente: number
  conector: Escolha
  pino: string
}

/** O fio, como o "Lançar pinout" o descreve (Q1, Q7). */
export type Fio = {
  /** Pelo id quando continua um fio que já existe; pelo nome quando é digitado. */
  circuito: Escolha
  cor: Escolha
  chicote: Escolha
  tipoPino: number
  obs: string
  /** O arquivo de onde o pinout foi tirado — fica fixo na sessão. */
  fonte: string
  /** `null` = ainda não escolhida; `0` = todos os modelos (Q6). */
  restricaoId: number | null
}

/**
 * O nome que o operador digitou puxa outros circuitos pelo começo? Quem entra num
 * fio que já existe não cria nada, então só pede confirmação o nome novo (Q5).
 */
export function precisaConfirmar(c: ConferenciaDoNome | null): boolean {
  return c !== null && c.grafias.length === 0 && c.alcanca.length + c.alcancadoPor.length > 0
}

const chaveDaPonta = (p: PontaDoFio) =>
  [p.componente.id || p.componente.texto.trim().toLowerCase(), p.conector.id || p.conector.texto.trim().toLowerCase(),
    p.pino.trim().toLowerCase()].join("|")

/**
 * O que falta para o fio ir para o lote. Vazio = pode. A ordem é a de quem
 * preenche: restrição, nome, depois as pontas.
 */
export function faltasDoFio(
  fio: Fio, pontas: PontaDoFio[],
  nome: { conferencia: ConferenciaDoNome | null; confirmado: boolean } = { conferencia: null, confirmado: false },
): string[] {
  const faltas: string[] = []
  if (fio.restricaoId === null) faltas.push("escolha a restrição no cabeçalho (ou 'Todos os modelos')")
  if (fio.circuito.id <= 0) {
    if (fio.circuito.texto.trim() === "") faltas.push("o nome do fio")
    else if (nome.conferencia === null) faltas.push("esperando a conferência do nome")
    else if (nome.conferencia.recusa) faltas.push(nome.conferencia.recusa)
    else if (nome.conferencia.grafias.length > 1) faltas.push(`escolha qual das ${nome.conferencia.grafias.length} grafias é o fio`)
    else if (precisaConfirmar(nome.conferencia) && !nome.confirmado) faltas.push("confirme que o fio também liga nos circuitos listados")
  }
  if (pontas.length === 0) faltas.push("pelo menos uma ponta")
  pontas.forEach((p, i) => {
    const qual = pontas.length > 1 ? `ponta ${i + 1}: ` : ""
    if (p.componente.id <= 0 && p.componente.texto.trim() === "") faltas.push(qual + "o componente")
    if (p.conector.id <= 0 && p.conector.texto.trim() === "") faltas.push(qual + "o conector")
    if (p.pino.trim() === "") faltas.push(qual + "o pino")
  })
  const vistas = new Set<string>()
  for (const p of pontas) {
    const k = chaveDaPonta(p)
    if (vistas.has(k)) { faltas.push(`a ponta ${p.componente.texto} ${p.conector.texto}·${p.pino} aparece duas vezes`); break }
    vistas.add(k)
  }
  return faltas
}

/** A ponta, no formato do `criarPino`. Id quando existe, texto quando é novo — nunca os dois. */
export function novoPinoDaPonta(fio: Fio, ponta: PontaDoFio): NovoPino {
  const porId = (e: Escolha) => (e.id > 0 ? { id: e.id, texto: "" } : { id: 0, texto: e.texto.trim() })
  const componente = porId(ponta.componente)
  const conector = porId(ponta.conector)
  const circuito = porId(fio.circuito)
  const cor = porId(fio.cor)
  const chicote = porId(fio.chicote)
  return {
    componenteId: componente.id,
    componenteCodigo: componente.texto,
    componenteTipo: componente.id > 0 ? 0 : ponta.tipoComponente,
    conectorId: conector.id,
    conectorDescricao: conector.texto,
    pino: ponta.pino.trim(),
    circuitoId: circuito.id,
    circuitoDescricao: circuito.texto,
    corId: cor.id,
    corDescricao: cor.texto,
    chicoteId: chicote.id,
    chicoteDescricao: chicote.texto,
    restricaoId: fio.restricaoId ?? 0,
    tipoPino: fio.tipoPino,
    obs: fio.obs.trim(),
    arquivoOriginal: fio.fonte.trim(),
  }
}

/**
 * O fio vira uma operação `criarPino` por ponta, com o texto que o lote mostra.
 * `rotuloDoTipo` vem de quem chama (a lista de tipos mora em `telas/pinout/tipos`,
 * e este arquivo não importa nada em tempo de execução).
 */
export function operacoesDoFio(
  fio: Fio, pontas: PontaDoFio[], nomeDoFio: string, rotuloDoTipo: (tipo: number) => string = String,
): { op: OperacaoDoEditor; texto: string }[] {
  return pontas.map((p) => ({
    op: { tipo: "criarPino", novoPino: novoPinoDaPonta(fio, p) },
    texto: `pino novo ${p.componente.texto.trim()}${p.componente.id > 0 ? "" : ` (componente novo: ${rotuloDoTipo(p.tipoComponente)})`} ` +
      `${p.conector.texto.trim()}·${p.pino.trim()} no fio '${nomeDoFio}'`,
  }))
}

// ------------------------------------------------------------------ renomear circuito no cadastro

/**
 * O que impede salvar o nome de um circuito no cadastro (a peça que a tela de
 * Cadastros e a gaveta do gráfico compartilham). As regras são as do fio novo
 * (Q4, Q5): nome que já é de OUTRO circuito é recusado — dois circuitos com o
 * mesmo nome se ligam pelo nome e são o começo dos 579 repetidos da base —, o
 * que a conferência do lote recusaria também, e o que o nome passa a puxar pelo
 * começo pede confirmação.
 *
 * `original` é o nome gravado (`null` ao incluir): sem mudança, nada a conferir.
 */
export function conferirRenomear(
  c: ConferenciaDoNome | null, nome: string, original: string | null,
): { bloqueio: string | null; confirmar: boolean } {
  const n = nome.trim()
  if (original !== null && n === original.trim()) return { bloqueio: null, confirmar: false }
  if (n === "") return { bloqueio: null, confirmar: false } // o "Obrigatório" do painel já cuida
  if (c === null) return { bloqueio: "conferindo o nome…", confirmar: false }
  if (c.recusa) return { bloqueio: c.recusa, confirmar: false }
  const outro = c.grafias[0]
  if (outro) {
    return {
      bloqueio: `Já existe o fio '${outro.descricao}' neste projeto (${outro.pinos} ponta${outro.pinos === 1 ? "" : "s"}). ` +
        "Dois circuitos com o mesmo nome se ligam pelo nome sem ser o mesmo fio — para juntar as pontas num fio só, " +
        "use \"Ligar a um pino…\" no editor de ligações.",
      confirmar: false,
    }
  }
  return { bloqueio: null, confirmar: precisaConfirmar(c) }
}

// ------------------------------------------------------------------ a junção nova (o assistente)
//
// A convenção da equipe (1.992 das 2.031 junções formadas da base, 26/09/2026):
// cada ramo são dois circuitos, BASE.RAMO na ponta distante e BASE.RAMO# no
// pino do componente da junção. O servidor escreve os nomes; aqui eles só são
// mostrados antes, para ninguém gravar sem ver.

const PROIBIDOS = /[.#%\\]/

/** A base é o nome antes do ponto (ex.: `E46T36`): sem ela a família dos ramos não existe. */
export function problemaNaBase(b: string): string | null {
  const t = b.trim()
  if (t === "") return "a base da junção (o nome do fio antes do ponto, ex.: E46T36)"
  if (PROIBIDOS.test(t)) return `a base '${t}' não pode ter ponto, #, % nem \\`
  return null
}

export function problemaNoRamo(r: string): string | null {
  const t = r.trim()
  if (t === "") return "há ramo sem nome"
  if (PROIBIDOS.test(t)) return `o ramo '${t}' não pode ter ponto, #, % nem \\`
  return null
}

/** Os dois fios de um ramo. */
export function nomesDoRamo(baseDaJuncao: string, ramo: string): { naPonta: string; naJuncao: string } {
  const naPonta = `${baseDaJuncao.trim()}.${ramo.trim()}`
  return { naPonta, naJuncao: naPonta + "#" }
}

/**
 * O próximo ramo, no jeito que a base já escreve: `E`, `E1`, `E2`…; `P1`, `P2` →
 * `P3`. A letra é a do primeiro ramo; o número é o maior usado mais um.
 */
export function sugerirRamo(anteriores: string[]): string {
  const primeiro = anteriores.find((r) => r.trim() !== "")?.trim()
  if (!primeiro) return "E"
  const letras = primeiro.replace(/\d+$/, "")
  const usados = anteriores
    .map((r) => r.trim())
    .filter((r) => r.replace(/\d+$/, "").toLowerCase() === letras.toLowerCase())
    .map((r) => Number(r.slice(letras.length) || "0"))
  return `${letras}${Math.max(0, ...usados) + 1}`
}

/** Um ramo como o assistente o tem na mão. */
export type RamoNaTela = {
  ramo: string
  pinoDaJuncao: string
  /** Conector do lado da junção, se não for o padrão. */
  conector: Escolha
  cor: Escolha
  /** A ponta distante que já está no desenho… */
  pontaPinoutId: number
  /** …ou a nova. */
  pontaNova: PontaDoFio | null
}

/** O lado da junção: o componente, o conector padrão dos pinos dela e o que vale para todos. */
export type LadoDaJuncao = {
  componente: Escolha
  conector: Escolha
  base: string
  chicote: Escolha
  tipoPino: number
  fonte: string
  restricaoId: number | null
}

export function faltasDaJuncao(lado: LadoDaJuncao, ramos: RamoNaTela[]): string[] {
  const faltas: string[] = []
  if (lado.restricaoId === null) faltas.push("escolha a restrição no cabeçalho (ou 'Todos os modelos')")
  if (lado.componente.id <= 0 && lado.componente.texto.trim() === "") faltas.push("o componente da junção")
  const semConector = ramos.some((r) => r.conector.id <= 0 && r.conector.texto.trim() === "")
  if (semConector && lado.conector.id <= 0 && lado.conector.texto.trim() === "") faltas.push("o conector da junção")
  const base = problemaNaBase(lado.base)
  if (base) faltas.push(base)
  if (ramos.length < 2) faltas.push("pelo menos dois ramos")
  ramos.forEach((r, i) => {
    const qual = `ramo ${r.ramo.trim() || i + 1}: `
    const p = problemaNoRamo(r.ramo)
    if (p) faltas.push(p)
    if (r.pinoDaJuncao.trim() === "") faltas.push(qual + "o pino na junção")
    if (r.pontaPinoutId <= 0) {
      const n = r.pontaNova
      if (!n) faltas.push(qual + "a ponta (um pino do desenho ou um pino novo)")
      else {
        if (n.componente.id <= 0 && n.componente.texto.trim() === "") faltas.push(qual + "o componente da ponta")
        if (n.conector.id <= 0 && n.conector.texto.trim() === "") faltas.push(qual + "o conector da ponta")
        if (n.pino.trim() === "") faltas.push(qual + "o pino da ponta")
      }
    }
  })
  const nomes = ramos.map((r) => r.ramo.trim().toLowerCase()).filter((r) => r !== "")
  const repetido = nomes.find((r, i) => nomes.indexOf(r) !== i)
  if (repetido) faltas.push(`o ramo '${repetido}' aparece duas vezes`)
  const pontas = ramos.map((r) => r.pontaPinoutId).filter((id) => id > 0)
  if (new Set(pontas).size !== pontas.length) faltas.push("dois ramos não podem chegar no mesmo pino")
  const naJuncao = ramos
    .filter((r) => r.pinoDaJuncao.trim() !== "")
    .map((r) => `${r.conector.id || r.conector.texto.trim().toLowerCase() || "padrão"}|${r.pinoDaJuncao.trim().toLowerCase()}`)
  if (new Set(naJuncao).size !== naJuncao.length) faltas.push("dois ramos no mesmo pino da junção")
  return faltas
}

/** A operação `criarJuncao`. Id quando existe, texto quando é novo — nunca os dois. */
export function operacaoDaJuncao(lado: LadoDaJuncao, ramos: RamoNaTela[]): OperacaoDoEditor {
  const porId = (e: Escolha) => (e.id > 0 ? { id: e.id, texto: "" } : { id: 0, texto: e.texto.trim() })
  const fioDoLado: Fio = {
    circuito: NADA, cor: NADA, chicote: lado.chicote, tipoPino: lado.tipoPino, obs: "", fonte: lado.fonte, restricaoId: lado.restricaoId,
  }
  const novoPino: NovoPino = novoPinoDaPonta(fioDoLado, { componente: lado.componente, tipoComponente: 3, conector: lado.conector, pino: "" })
  const ramosDaOp: RamoDaJuncao[] = ramos.map((r) => {
    const c = porId(r.conector)
    const cor = porId(r.cor)
    return {
      ramo: r.ramo.trim(),
      pinoDaJuncao: r.pinoDaJuncao.trim(),
      conectorId: c.id,
      conectorDescricao: c.texto,
      corId: cor.id,
      corDescricao: cor.texto,
      pontaPinoutId: r.pontaPinoutId > 0 ? r.pontaPinoutId : 0,
      ponta: r.pontaPinoutId > 0 || !r.pontaNova ? null : novoPinoDaPonta({ ...fioDoLado, cor: r.cor }, r.pontaNova),
    }
  })
  return { tipo: "criarJuncao", base: lado.base.trim(), ramos: ramosDaOp, novoPino }
}

// ------------------------------------------------------------------ o desenho

export const LARGURA_CAIXA = 232
export const CABECALHO = 30
export const LINHA = 22
const FOLGA_X = 128
const FOLGA_Y = 22
const MARGEM = 24

export type PinoNaCaixa = { no: NoDoGrafo; y: number }
export type JuncaoNaCaixa = { chave: string; etiqueta: string; de: number; ate: number }

export type Caixa = {
  componenteId: number
  codigo: string
  titulo: string
  tipo: number
  raiz: boolean
  coluna: number
  x: number
  y: number
  largura: number
  altura: number
  pinos: PinoNaCaixa[]
  juncoes: JuncaoNaCaixa[]
}

export type Layout = {
  caixas: Caixa[]
  largura: number
  altura: number
  /** Onde fica cada pino: y do centro da linha e as bordas da caixa. */
  pinos: Map<string, { y: number; xEsquerda: number; xDireita: number; caixa: Caixa }>
}

const comparador = new Intl.Collator("pt-BR", { numeric: true, sensitivity: "base" })

/**
 * Cada componente vira uma caixa com os seus pinos; as caixas vão em colunas
 * pela distância até o componente de partida, que fica na primeira. É o mesmo
 * arranjo mental do desenho do motor — o operador já lê assim — sem os 18
 * segundos do SVG.
 *
 * `posicoes` são as caixas que a pessoa arrastou: ganham do cálculo.
 */
export function montarLayout(g: GrafoDoPinout, posicoes: Record<number, { x: number; y: number }> = {}): Layout {
  const componenteDoNo = new Map(g.nos.map((n) => [n.id, n.componenteId]))
  const porComponente = new Map<number, NoDoGrafo[]>()
  for (const n of g.nos) {
    const l = porComponente.get(n.componenteId)
    if (l) l.push(n)
    else porComponente.set(n.componenteId, [n])
  }

  // Distância de cada componente até o de partida, pelas arestas.
  const vizinhos = new Map<number, Set<number>>()
  for (const a of g.arestas) {
    const x = componenteDoNo.get(a.de)
    const y = componenteDoNo.get(a.para)
    if (x === undefined || y === undefined || x === y) continue
    if (!vizinhos.has(x)) vizinhos.set(x, new Set())
    vizinhos.get(x)!.add(y)
  }
  const coluna = new Map<number, number>()
  const ordem: number[] = []
  const fila: number[] = []
  for (const [c, nos] of porComponente) {
    if (nos.some((n) => n.raiz)) { coluna.set(c, 0); ordem.push(c); fila.push(c) }
  }
  while (fila.length > 0) {
    const c = fila.shift()!
    for (const v of vizinhos.get(c) ?? []) {
      if (!coluna.has(v)) { coluna.set(v, coluna.get(c)! + 1); ordem.push(v); fila.push(v) }
    }
  }
  const ultima = Math.max(0, ...coluna.values())
  for (const c of porComponente.keys()) {
    if (!coluna.has(c)) { coluna.set(c, ultima + 1); ordem.push(c) }
  }

  const alturaDaColuna = new Map<number, number>()
  const caixas: Caixa[] = []
  const pinos = new Map<string, { y: number; xEsquerda: number; xDireita: number; caixa: Caixa }>()

  for (const c of ordem) {
    const nos = [...porComponente.get(c)!].sort((a, b) =>
      comparador.compare(a.conector, b.conector) || comparador.compare(a.pino, b.pino))
    const col = coluna.get(c)!
    const altura = CABECALHO + nos.length * LINHA + 6
    const yCalculado = alturaDaColuna.get(col) ?? MARGEM
    alturaDaColuna.set(col, yCalculado + altura + FOLGA_Y)

    const arrastada = posicoes[c]
    const x = arrastada?.x ?? MARGEM + col * (LARGURA_CAIXA + FOLGA_X)
    const y = arrastada?.y ?? yCalculado
    const primeiro = nos[0]!

    const caixa: Caixa = {
      componenteId: c,
      codigo: primeiro.componenteCodigo,
      titulo: primeiro.componente,
      tipo: primeiro.componenteTipo,
      raiz: nos.some((n) => n.raiz),
      coluna: col,
      x, y,
      largura: LARGURA_CAIXA,
      altura,
      pinos: nos.map((no, i) => ({ no, y: y + CABECALHO + i * LINHA + LINHA / 2 })),
      juncoes: [],
    }

    // Os ramos da mesma junção ficam amarrados por um colchete na caixa.
    const porGrupo = new Map<string, number[]>()
    caixa.pinos.forEach((p) => {
      if (!p.no.grupoDeJuncao) return
      const l = porGrupo.get(p.no.grupoDeJuncao)
      if (l) l.push(p.y)
      else porGrupo.set(p.no.grupoDeJuncao, [p.y])
    })
    for (const [chave, ys] of porGrupo) {
      if (ys.length < 2) continue
      caixa.juncoes.push({ chave, etiqueta: chave.split("|")[2] ?? "", de: Math.min(...ys), ate: Math.max(...ys) })
    }

    caixas.push(caixa)
    for (const p of caixa.pinos) pinos.set(p.no.id, { y: p.y, xEsquerda: x, xDireita: x + LARGURA_CAIXA, caixa })
  }

  const largura = Math.max(...caixas.map((c) => c.x + c.largura), 400) + MARGEM + 40
  const altura = Math.max(...caixas.map((c) => c.y + c.altura), 200) + MARGEM
  return { caixas, largura, altura, pinos }
}

/**
 * O traço de uma aresta. Entre caixas diferentes, sai pela borda que fica de
 * frente para a outra; dentro da mesma caixa (os ramos de uma junção), faz um
 * arco pela direita.
 */
export function tracado(l: Layout, a: ArestaDoGrafo): string | null {
  const de = l.pinos.get(a.de)
  const para = l.pinos.get(a.para)
  if (!de || !para) return null
  if (de.caixa === para.caixa) {
    const x = de.xDireita
    const bojo = 26 + Math.min(40, Math.abs(para.y - de.y) / 6)
    return `M ${x} ${de.y} C ${x + bojo} ${de.y}, ${x + bojo} ${para.y}, ${x} ${para.y}`
  }
  const praDireita = para.caixa.x >= de.caixa.x
  const x1 = praDireita ? de.xDireita : de.xEsquerda
  const x2 = praDireita ? para.xEsquerda : para.xDireita
  const meio = Math.max(40, Math.abs(x2 - x1) / 2)
  const c1 = praDireita ? x1 + meio : x1 - meio
  const c2 = praDireita ? x2 - meio : x2 + meio
  return `M ${x1} ${de.y} C ${c1} ${de.y}, ${c2} ${para.y}, ${x2} ${para.y}`
}
